import React, { forwardRef, ForwardRefRenderFunction, PropsWithChildren } from 'react';
import { Icon, IconName } from '../icons/icon/icon-component';
import styles from './chip-component.module.scss';

type ChipProps = PropsWithChildren<{
  /** The text displayed in the chip */
  text: string;
  /** The icon at the end of the chip */
  icon?: IconName;
  disabled?: boolean;
  isValid?: boolean;
  /** Called when the body of the chip is pressed */
  onClick?: () => void;
  /** Called when the chip icon is pressed */
  onIconClickCallback?: () => void;
  /** Whether or not the chip should be styled as active even when it is focused (used in the Chip Holder) */
  activeOnFocus?: boolean;

  id?: string;
}>;

/**
 * Chip Atom which can render text and an optional icon.
 *
 * https://www.figma.com/file/Op3Jr1EuRxDkL3bHKsp5qC/EM-Atoms?type=design&node-id=4892-207610
 */
export const ChipForwardedRef: ForwardRefRenderFunction<HTMLDivElement, ChipProps> = (props: ChipProps, ref) => {
  return (
    <div
      tabIndex={-1}
      data-testid='chip'
      ref={ref}
      className={`${styles['chip-container']} ${props.disabled ? styles.disabled : ''} ${
        (props.isValid ?? true) || props.disabled ? '' : styles.error
      } ${props.activeOnFocus ? styles['active-on-focus'] : ''}`}
      onClick={clickHandler}
      id={props.id}
      aria-disabled={props.disabled}
      aria-invalid={!(props.isValid ?? true)}
    >
      <span className={styles['chip-text']}>{props.text}</span>
      {props.icon && (
        <Icon data-testid='chip-icon' name={props.icon} width={12} height={12} className={styles['chip-icon']} onClick={iconClickHandler} />
      )}
    </div>
  );

  function clickHandler() {
    if (!props.disabled && props.onClick) {
      props.onClick();
    }
  }

  function iconClickHandler(e: React.MouseEvent<SVGElement, MouseEvent>) {
    if (!props.disabled && props.onIconClickCallback) {
      e.stopPropagation(); // Stops the main chip click handler `clickHandler` from being called as well
      props.onIconClickCallback();
    }
  }
};

export const Chip = forwardRef(ChipForwardedRef);

export default Chip;
